import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { api } from "../utils/api";

// iRacing track picker (#282): search + category filter + venue-grouped rows that
// expand to their configs. The selectable unit is always a CONFIG (config-level
// track_id) — never the venue.

interface Config { trackId: number; configName: string | null; category: string; free: boolean; }
interface Venue { packageId: number | null; trackName: string; configs: Config[]; }
export interface PickedTrack { trackId: number; trackName: string; configName: string | null; category: string; }

const CATS = [
  { id: "", label: "All" },
  { id: "oval", label: "Oval" },
  { id: "road", label: "Road" },
  { id: "dirt_oval", label: "Dirt Oval" },
  { id: "dirt_road", label: "Dirt Road" },
];

export function TrackPicker({
  selected,
  onSelect,
}: {
  selected: PickedTrack | null;
  onSelect: (t: PickedTrack) => void;
}) {
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<number | string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => { searchRef.current?.focus(); }, []);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      setLoading(true);
      const params = new URLSearchParams();
      if (category) params.set("category", category);
      if (q.trim()) params.set("q", q.trim());
      api<{ venues: Venue[] }>(`/api/tracks?${params.toString()}`)
        .then((d) => !cancelled && setVenues(Array.isArray(d.venues) ? d.venues : []))
        .catch(() => !cancelled && setVenues([]))
        .finally(() => !cancelled && setLoading(false));
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [category, q]);

  return (
    <div style={styles.wrap}>
      <input
        ref={searchRef}
        className="input"
        placeholder="Search tracks…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        aria-label="Search tracks"
      />
      <div style={styles.chips} role="radiogroup" aria-label="Track category">
        {CATS.map((c) => (
          <button
            key={c.id || "all"}
            type="button"
            role="radio"
            aria-checked={category === c.id}
            className={`pill ${category === c.id ? "pill--live" : ""}`}
            style={styles.chip}
            onClick={() => setCategory(c.id)}
          >
            {c.label}
          </button>
        ))}
      </div>
      <div style={styles.list}>
        {loading && <div style={styles.muted}>Loading tracks…</div>}
        {!loading && venues.length === 0 && <div style={styles.muted}>No tracks match.</div>}
        {!loading && venues.map((v) => {
          const key = v.packageId ?? v.trackName;
          const isOpen = expanded === key;
          const single = v.configs.length === 1;
          const hasSelected = v.configs.some((c) => c.trackId === selected?.trackId);
          return (
            <div key={key} style={styles.venue}>
              <button
                type="button"
                style={{ ...styles.venueRow, ...(hasSelected ? styles.venueSelected : null) }}
                onClick={() =>
                  single
                    ? onSelect({ trackId: v.configs[0].trackId, trackName: v.trackName, configName: v.configs[0].configName, category: v.configs[0].category })
                    : setExpanded(isOpen ? null : key)
                }
              >
                <span style={styles.venueName}>{v.trackName}</span>
                <span style={styles.count}>{single ? (v.configs[0].configName || "Select") : `${v.configs.length} layouts`}</span>
              </button>
              {isOpen && !single && v.configs.map((cfg) => {
                const on = selected?.trackId === cfg.trackId;
                return (
                  <button
                    key={cfg.trackId}
                    type="button"
                    style={{ ...styles.configRow, ...(on ? styles.configOn : null) }}
                    onClick={() => onSelect({ trackId: cfg.trackId, trackName: v.trackName, configName: cfg.configName, category: cfg.category })}
                  >
                    <span>{cfg.configName || "Default"}</span>
                    {cfg.free && <span style={styles.free}>Free</span>}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  wrap: { display: "flex", flexDirection: "column", gap: "0.6rem" },
  chips: { display: "flex", flexWrap: "wrap", gap: "0.4rem" },
  chip: { cursor: "pointer", border: "1px solid var(--rule)" },
  list: { maxHeight: "320px", overflowY: "auto", border: "1px solid var(--rule)", borderRadius: "var(--r-md)" },
  muted: { padding: "1rem", color: "var(--text-muted)", fontSize: "0.875rem" },
  venue: { borderBottom: "1px solid var(--rule-faint)" },
  venueRow: {
    width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
    gap: "0.6rem", padding: "0.65rem 0.85rem", background: "transparent", border: "none",
    cursor: "pointer", color: "var(--text-default)", fontFamily: "var(--font-body)",
    fontSize: "0.9rem", textAlign: "left",
  },
  venueSelected: { background: "var(--live-soft)" },
  venueName: { fontWeight: 600 },
  count: { color: "var(--text-faint)", fontSize: "0.8125rem", flexShrink: 0 },
  configRow: {
    width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "0.5rem 0.85rem 0.5rem 1.6rem", background: "var(--ink-1)", border: "none",
    borderTop: "1px solid var(--rule-faint)", cursor: "pointer",
    color: "var(--text-secondary)", fontSize: "0.85rem", textAlign: "left",
  },
  configOn: { background: "var(--live)", color: "var(--ink-0)", fontWeight: 600 },
  free: { fontSize: "0.7rem", color: "var(--text-faint)", border: "1px solid var(--rule)", borderRadius: "999px", padding: "0 0.4rem" },
};
